import { Box, Grid, MenuItem, Stack, Typography } from "@mui/material";
import React, { memo, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GlobalButton from "../../../components/button";
import CustomCheckbox from "../../../components/checkbox";
import Label from "../../../components/label";
import AppLoader from "../../../components/loader";
import CustomDropDown from "../../../components/select";
import { AppContext } from "../../../lib/Context";
import EnergyField from "./EnergyField";

const OtherProducts = () => {
  const location = useLocation();
  const { otherProducts, setOtherProducts, userData } = useContext(AppContext);
  const isAdmin = userData?.designation === "Admin" ? true : false;
  const isDetailPage = location.pathname === "/prospect-details";

  const handleProductChange = (event) => {
    const selectedValue = event.target.value;
    if (
      selectedValue &&
      !otherProducts.selectedProducts.includes(selectedValue)
    ) {
      setOtherProducts((prev) => ({
        ...prev,
        selectedProducts: [...prev.selectedProducts, selectedValue],
      }));
    }
  };

  const handleEnergyOptionChange = (option) => {
    setOtherProducts((prev) => ({
      ...prev,
      energy: {
        ...prev.energy,
        [option]: {
          ...prev.energy[option],
          isSelected: !prev.energy[option].isSelected,
        },
      },
    }));
  };

  const handleTelecomTypeChange = (type) => {
    setOtherProducts((prev) => ({
      ...prev,
      telecoms: {
        ...prev.telecoms,
        business: {
          ...prev.telecoms.business,
          isSelected:
            type === "business" ? !prev.telecoms.business.isSelected : false,
        },
        residential: {
          ...prev.telecoms.residential,
          isSelected:
            type === "residential"
              ? !prev.telecoms.residential.isSelected
              : false,
        },
      },
    }));
  };

  const handleInputChange =
    (productType, subType = null) =>
    (e) => {
      const { name, value } = e.target;

      if (productType === "energy") {
        setOtherProducts((prev) => ({
          ...prev,
          energy: {
            ...prev.energy,
            [subType]: {
              ...prev.energy[subType],
              [name]: value,
            },
          },
        }));
      } else if (productType === "telecoms") {
        setOtherProducts((prev) => ({
          ...prev,
          telecoms: {
            ...prev.telecoms,
            [subType]: {
              ...prev.telecoms[subType],
              [name]: value,
            },
          },
        }));
      } else {
        setOtherProducts((prev) => ({
          ...prev,
          [productType]: {
            ...prev[productType],
            [name]: value,
          },
        }));
      }
    };

  const handleRemoveProduct = (productToRemove) => {
    setOtherProducts((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter(
        (product) => product !== productToRemove
      ),
    }));
  };

  const products = ["Energy", "Telecoms", "Card Processing", "Mobile"];

  return (
    <Box my={3}>
      <Typography variant="h6" color={"text.charcoalGrey"}>
        Do You Want To Add Other Products?
      </Typography>
      <Grid item xs={12} sm={6} md={3}>
        <Label title={"Other Products"} htmlFor={"other-products"} />
        <CustomDropDown
          id={"other-products"}
          variant={"filled"}
          value=""
          name={"otherProducts"}
          onChange={handleProductChange}
          sx={{ width: "310px" }}
          readonly={isDetailPage}
        >
          {products.map((val, ind) => (
            <MenuItem
              key={ind}
              value={val}
              disabled={otherProducts.selectedProducts.includes(val)}
            >
              {val}
            </MenuItem>
          ))}
        </CustomDropDown>
      </Grid>

      {otherProducts.selectedProducts.map((product, index) => (
        <Box key={index} mt={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{product}</Typography>
            <GlobalButton
              variant="outlined"
              title="Remove"
              handleClickBtn={() => handleRemoveProduct(product)}
              disabled={isDetailPage}
              style={{
                display: "flex",
                width: { xs: "100%", sm: 110 },
                fontSize: "1rem",
                alignSelf: "flex-end",
                mt: 0,
                padding: "0px 10px",
              }}
            />
          </Stack>

          {product === "Energy" && (
            <>
              <Stack direction={"row"} alignItems={"center"} spacing={4} mt={2}>
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <CustomCheckbox
                    isChecked={otherProducts.energy.electricity.isSelected}
                    handleChecked={() => {
                      if (!isDetailPage) {
                        handleEnergyOptionChange("electricity");
                      }
                    }}
                  />
                  <Typography>Electricity</Typography>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <CustomCheckbox
                    isChecked={otherProducts.energy.gas.isSelected}
                    handleChecked={() => {
                      handleEnergyOptionChange("gas")}}
                  />
                  <Typography>Gas</Typography>
                </Stack>
              </Stack>

              {otherProducts.energy.electricity.isSelected && (
                <EnergyField
                  showTitle={true}
                  title={"Electricity"}
                  onInputChange={handleInputChange("energy", "electricity")}
                  values={otherProducts.energy.electricity}
                />
              )}
              {otherProducts.energy.gas.isSelected && (
                <EnergyField
                  showTitle={true}
                  title={"Gas"}
                  onInputChange={handleInputChange("energy", "gas")}
                  values={otherProducts.energy.gas}
                />
              )}
            </>
          )}

          {product === "Telecoms" && (
            <>
              <Stack direction={"row"} alignItems={"center"} spacing={4} mt={2}>
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <CustomCheckbox
                    isChecked={otherProducts.telecoms.business.isSelected}
                    handleChecked={() => handleTelecomTypeChange("business")}
                  />
                  <Typography>Business</Typography>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={1}>
                  <CustomCheckbox
                    isChecked={otherProducts.telecoms.residential.isSelected}
                    handleChecked={() => handleTelecomTypeChange("residential")}
                  />
                  <Typography>Residential</Typography>
                </Stack>
              </Stack>

              {otherProducts.telecoms.business.isSelected && (
                <EnergyField
                  showTitle={true}
                  title="Business Telecoms"
                  onInputChange={handleInputChange("telecoms", "business")}
                  values={otherProducts.telecoms.business}
                />
              )}

              {otherProducts.telecoms.residential.isSelected && (
                <EnergyField
                  showTitle={true}
                  title="Residential Telecoms"
                  onInputChange={handleInputChange("telecoms", "residential")}
                  values={otherProducts.telecoms.residential}
                />
              )}
            </>
          )}

          {product === "Card Processing" && (
            <EnergyField
              showTitle={false}
              title={"Card Processing"}
              onInputChange={handleInputChange("cardProcessing")}
              values={otherProducts.cardProcessing}
            />
          )}

          {product === "Mobile" && (
            <EnergyField
              showTitle={false}
              title={"Mobile"}
              onInputChange={handleInputChange("mobile")}
              values={otherProducts.mobile}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default memo(OtherProducts);
